import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"
import { dragHasFiles, uploadTaskAttachments } from "../utils/task_attachments"
import { notesToHtml } from "../utils/inline_markdown"
import { openAssignPopover, openDatePopover, openPickPopover, remindTimeOf } from "../utils/task_popovers"

// The task pane (task_panels/_panel): one sidebar for a task's details, note,
// subtasks, files and comments. The thread and the files keep their own
// controllers (task-comments, task-attachments) on this same element; this one
// owns the rest:
//
//   - the section tabs, and scrolling to the section the pane was opened for,
//     unfolding it first if the reader had folded it (fold-section)
//   - the note: rendered at rest, edited as its markdown source on click, saved
//     on blur - what the row's inline note under the title used to do
//   - subtask (and the task's own) checkboxes
//   - the whole pane as a drop target for files, and pasted images
//
// Writes that the outline also makes go THROUGH the outline, so the row, its
// chips and live sync stay in step - it answers by setting detail.handled:
//
//   jane:task-pane-notes   { taskId, notes }    save the note
//   jane:task-pane-toggle  { taskId, completed }  complete / reopen
//   jane:task-pane-action  { taskId, action, anchor }  open the row's Date
//                                              ("date") or Assign ("assign")
//                                              popover beside the button, its
//                                              tag picker ("tags") or company
//                                              picker ("company")
//   jane:task-pane-update  { taskId, priority } or { taskId, fieldId,
//                                              fieldValue }  set priority or a
//                                              custom field
//   jane:task-pane-open    { taskId, section }  open another task's pane
//
// Off the outline (the board, mind map or galaxy, or a filtered-out parent)
// nothing answers, so the pane does it itself: the note and the checkboxes
// send their own requests (a completion is announced as
// jane:task-completed-changed for those views to repaint, utils/task_pane.js),
// and the Date, Assign, Tags and Company buttons open the same pickers the row
// uses (utils/task_popovers.js), writing through the pane.

// The nearest ancestor that actually scrolls: which one depends on how the
// sidebar is shown (sidebar, drawer, floating window).
function scrollParent(el) {
    for (let node = el.parentElement; node; node = node.parentElement) {
        const { overflowY } = getComputedStyle(node)
        if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) return node
    }
    return document.scrollingElement
}

export default class extends Controller {
    static targets = ["tab", "section", "notes", "overlay", "subtaskCount", "subtaskBar", "newSubtask"]
    static values = {
        taskId: Number, section: String, editable: Boolean, notesUrl: String,
        // The task's values for the pane's own pickers (task_panels/_panel).
        listId: Number, due: String, remind: String, recurrenceKind: String, recurrenceWeekday: String,
        recurrenceInterval: String, assignees: String, tags: Array, candidates: Array
    }

    connect() {
        this._scroller = scrollParent(this.element)
        this._onTaskChanged = (event) => {
            if (Number(event.detail?.taskId) === this.taskIdValue) this._refreshDetails()
        }
        window.addEventListener("jane:task-changed", this._onTaskChanged)
        this._onScroll = () => { this._syncTabs(); this._syncStuck() }
        this._scroller?.addEventListener("scroll", this._onScroll, { passive: true })
        this._markTab(this.sectionValue || "details")
        this._syncStuck()
        if (this.sectionValue && this.sectionValue !== "details") {
            requestAnimationFrame(() => this._scrollTo(this.sectionValue, { smooth: false }))
        } else {
            // A frame later, so it runs after fold-section has restored the
            // reader's folds: the section the pane was opened for shows.
            requestAnimationFrame(() => this._unfold(this._section("details")))
        }
        if (this.sectionValue === "notes" && this.hasNotesTarget && this.notesTarget.dataset.edit === "1") {
            requestAnimationFrame(() => this._startEditing())
        }
    }

    disconnect() {
        clearTimeout(this._refreshTimer)
        window.removeEventListener("jane:task-changed", this._onTaskChanged)
        this._scroller?.removeEventListener("scroll", this._onScroll)
        if (this.hasNotesTarget && this.notesTarget.isContentEditable) this._commitNotes()
    }

    // --- Sections ---

    go(e) {
        const section = e.currentTarget.dataset.section
        this._markTab(section)
        this._scrollTo(section)
    }

    _section(section) {
        return this.sectionTargets.find((s) => s.dataset.section === section)
    }

    // An empty Notes opened - by its heading or a tab - is a note about to be
    // written: start editing it.
    sectionToggled(e) {
        const section = e.currentTarget
        if (!section.open || section.dataset.section !== "notes" || !this.editableValue || !this.hasNotesTarget) return
        if (this.notesTarget.isContentEditable || (this.notesTarget.dataset.source || "").trim()) return
        this._startEditing()
    }

    // Sections fold (task_panels/_section); going to one opens it. This is
    // not remembered as the reader's choice - only a click on the heading is.
    _unfold(el) {
        if (el && !el.open) el.open = true
    }

    _scrollTo(section, { smooth = true } = {}) {
        const el = this._section(section)
        if (!el) return
        this._unfold(el)
        this._pinnedTab = section
        clearTimeout(this._pinTimer)
        this._pinTimer = setTimeout(() => { this._pinnedTab = null }, 700)
        el.scrollIntoView({ block: "start", behavior: smooth ? "smooth" : "auto" })
        el.classList.remove("is-flash")
        void el.offsetWidth
        el.classList.add("is-flash")
        if (section === "comments") requestAnimationFrame(() => this.element.querySelector("[data-task-comments-target='input']")?.focus({ preventScroll: true }))
    }

    // The tab for the section at the top of the scroll area, except straight
    // after a click, while the smooth scroll is still on its way there.
    _syncTabs() {
        if (this._pinnedTab) return
        const top = this._scroller?.getBoundingClientRect().top ?? 0
        const navBottom = this.element.querySelector(".task-pane-nav")?.getBoundingClientRect().bottom ?? top
        let current = this.sectionTargets[0]?.dataset.section
        this.sectionTargets.forEach((s) => {
            if (s.getBoundingClientRect().top <= navBottom + 24) current = s.dataset.section
        })
        const atEnd = this._scroller && this._scroller.scrollTop + this._scroller.clientHeight >= this._scroller.scrollHeight - 4
        if (atEnd) current = this.sectionTargets[this.sectionTargets.length - 1]?.dataset.section
        this._markTab(current)
    }

    // Pinned to the top, the tab strip would run under the sidebar's own
    // buttons (View as, expand, close); while it is, its tabs drop below them.
    _syncStuck() {
        const nav = this.element.querySelector(".task-pane-nav")
        if (!nav || !this._scroller) return
        const actions = this.element.closest(".detail-sidebar")?.querySelector(".detail-sidebar-actions")
        const stuck = nav.getBoundingClientRect().top <= this._scroller.getBoundingClientRect().top + 1 && this._scroller.scrollTop > 0
        nav.classList.toggle("is-stuck", stuck)
        const below = actions ? Math.max(0, actions.getBoundingClientRect().bottom - this._scroller.getBoundingClientRect().top) : 0
        // On the pane, so a section scrolled to lands below the taller strip.
        this.element.style.setProperty("--tp-actions-h", `${Math.round(below)}px`)
    }

    _markTab(section) {
        this.tabTargets.forEach((tab) => {
            const on = tab.dataset.section === section
            tab.classList.toggle("is-active", on)
            if (on) tab.setAttribute("aria-current", "true")
            else tab.removeAttribute("aria-current")
        })
    }

    // --- Note ---

    editNotes(e) {
        const note = this.notesTarget
        if (note.isContentEditable) return
        if (e.type === "click" && e.target.closest("a[href]")) return
        const sel = window.getSelection()
        if (e.type === "click" && sel && !sel.isCollapsed && note.contains(sel.anchorNode)) return
        this._startEditing()
    }

    _startEditing() {
        const note = this.notesTarget
        note.setAttribute("contenteditable", "true")
        note.classList.add("is-editing")
        note.textContent = note.dataset.source || ""
        note.focus()
        const range = document.createRange()
        range.selectNodeContents(note)
        range.collapse(false)
        const sel = window.getSelection()
        sel.removeAllRanges()
        sel.addRange(range)
    }

    // Escape finishes (blur saves). The title's formatting keys write markdown
    // around the selection, as the row's note did.
    notesKeydown(e) {
        if (!this.notesTarget.isContentEditable) {
            if (e.key === "Enter") { e.preventDefault(); this._startEditing() }
            return
        }
        e.stopPropagation()
        if (e.key === "Escape") {
            e.preventDefault()
            this.notesTarget.blur()
            return
        }
        const mod = e.metaKey || e.ctrlKey
        if (!mod || e.altKey) return
        const key = e.key.toLowerCase()
        const wrap = (!e.shiftKey && key === "b" && ["**", "**"]) ||
            (!e.shiftKey && key === "i" && ["*", "*"]) ||
            (!e.shiftKey && key === "e" && ["`", "`"]) ||
            (e.shiftKey && key === "x" && ["~~", "~~"]) ||
            (!e.shiftKey && key === "k" && ["[", "](https://)"])
        if (!wrap) return
        e.preventDefault()
        const text = window.getSelection()?.toString() || ""
        document.execCommand("insertText", false, `${wrap[0]}${text}${wrap[1]}`)
    }

    // Plain text only; a URL pasted over selected words links them. A pasted
    // file is the pane's paste handler's (it attaches it).
    notesPaste(e) {
        if (!this.notesTarget.isContentEditable || !e.clipboardData) return
        if (e.clipboardData.files?.length && !e.clipboardData.getData("text/plain")) return
        e.preventDefault()
        e.stopPropagation()
        const text = e.clipboardData.getData("text/plain")
        const selected = window.getSelection()?.toString() || ""
        const url = text.trim()
        if (selected && !selected.includes("\n") && /^https?:\/\/\S+$/i.test(url)) {
            document.execCommand("insertText", false, `[${selected.replace(/([\\\]\[])/g, "\\$1")}](${url})`)
        } else {
            document.execCommand("insertText", false, text)
        }
    }

    saveNotes() {
        this._commitNotes()
    }

    _commitNotes() {
        const note = this.notesTarget
        if (!note.isContentEditable) return
        // innerText (not textContent) so contenteditable line breaks survive.
        const text = note.innerText.replace(/ /g, " ").replace(/\n{3,}/g, "\n\n").trim()
        const had = (note.dataset.source || "").trim()
        note.setAttribute("contenteditable", "false")
        note.classList.remove("is-editing")
        note.dataset.source = text
        note.classList.toggle("is-empty", !text)
        note.innerHTML = text ? notesToHtml(text) : ""
        if (text === had) return

        const detail = { taskId: this.taskIdValue, notes: text, handled: false }
        window.dispatchEvent(new CustomEvent("jane:task-pane-notes", { detail }))
        if (detail.handled) return
        const body = new FormData()
        body.set("notes", text)
        fetch(this.notesUrlValue, { method: "PATCH", headers: this._headers(), body, credentials: "same-origin" }).catch(() => {})
    }

    // --- Checkboxes ---

    toggleTask(e) {
        const box = e.currentTarget
        const id = Number(box.dataset.taskId)
        const completed = box.dataset.completed !== "true"
        this._paintCheck(box, completed)
        const detail = { taskId: id, completed, handled: false }
        window.dispatchEvent(new CustomEvent("jane:task-pane-toggle", { detail }))
        if (detail.handled) return
        fetch(this._withList(`/tasks/${id}/${completed ? "complete" : "uncomplete"}.json`), { method: "POST", headers: this._headers(), credentials: "same-origin" })
            .then((r) => {
                if (!r.ok) return this._paintCheck(box, !completed)
                window.dispatchEvent(new CustomEvent("jane:task-completed-changed", { detail: { taskId: id, completed } }))
            })
            .catch(() => this._paintCheck(box, !completed))
    }

    _paintCheck(box, completed) {
        box.dataset.completed = String(completed)
        box.classList.toggle("is-done", completed)
        box.setAttribute("aria-pressed", String(completed))
        box.closest(".task-pane-subtask")?.classList.toggle("is-done", completed)
        const subs = this.element.querySelectorAll(".task-pane-subtask")
        if (!subs.length) return
        const done = this.element.querySelectorAll(".task-pane-subtask.is-done").length
        if (this.hasSubtaskCountTarget) this.subtaskCountTarget.textContent = `${done}/${subs.length}`
        if (this.hasSubtaskBarTarget) this.subtaskBarTarget.style.width = `${Math.round(100 * done / subs.length)}%`
    }

    // --- Adding a subtask ---
    //
    // Enter adds it at the end; the field stays for the next one. The request
    // carries no live-sync client id, so the list behind the pane inserts the
    // row from its own broadcast like a teammate's.

    subtaskKeydown(e) {
        if (e.key === "Escape") { e.target.value = ""; e.target.blur(); return }
        if (e.key !== "Enter" || e.isComposing) return
        e.preventDefault()
        const title = e.target.value.trim()
        if (!title || this._addingSubtask) return
        this._addingSubtask = true
        const fd = new FormData()
        fd.set("title", title)
        fd.set("parent_id", String(this.taskIdValue))
        const last = Array.from(this.element.querySelectorAll(".task-pane-subtask [data-task-id]")).pop()
        if (last) fd.set("after_id", last.dataset.taskId)
        const headers = { Accept: "application/json", "X-CSRF-Token": csrfToken() }
        fetch(this._withList("/tasks.json"), { method: "POST", headers, body: fd, credentials: "same-origin" })
            .then((r) => r.json())
            .catch(() => null)
            .then((data) => {
                this._addingSubtask = false
                if (!data?.ok) return
                e.target.value = ""
                this._refocusSubtask = true
                this._refreshDetails()
            })
    }

    // --- Details and navigation, handed to the outline ---

    // The Date and Assign popovers open beside the button (anchor).
    // On the list, the row opens its own picker (its rail and undo stay
    // right). Anywhere else - the board, mind map, galaxy, or a row filtered
    // out of view - the pane opens the same pickers itself.
    rowAction(e) {
        e.preventDefault()
        const anchor = e.currentTarget
        const action = anchor.dataset.paneAction
        const detail = { taskId: this.taskIdValue, action, anchor, handled: false }
        window.dispatchEvent(new CustomEvent("jane:task-pane-action", { detail }))
        if (detail.handled) return
        if (action === "date") this._pickDate(anchor)
        else if (action === "assign") this._pickAssignees(anchor)
        else if (action === "tags") this._pickTags(anchor)
        else if (action === "company") this._pickCompany(anchor)
    }

    _pickDate(anchor) {
        openDatePopover({
            anchor,
            fallback: anchor,
            values: {
                dueDate: this.dueValue,
                remindTime: remindTimeOf(this.remindValue),
                recurrenceKind: this.recurrenceKindValue,
                recurrenceWeekday: this.recurrenceWeekdayValue,
                recurrenceInterval: this.recurrenceIntervalValue
            },
            onSave: (fd) => this._patchTask(fd)
        })
    }

    _pickAssignees(anchor) {
        openAssignPopover({
            anchor,
            fallback: anchor,
            candidates: this.candidatesValue,
            selected: this.assigneesValue.split(",").filter(Boolean),
            chipFor: (candidate) => this._chip(candidate),
            onToggle: async (next) => {
                const fd = new FormData()
                fd.set("assignees", next.join(","))
                const data = await this._patchTask(fd)
                return data?.ok ? (data.task.assignees || []) : null
            }
        })
    }

    // Tags toggle one after another; a new name creates the tag.
    _pickTags(anchor) {
        const toggle = async (slug) => {
            const tags = this.tagsValue
            const next = tags.some((t) => t.toLowerCase() === slug.toLowerCase()) ? tags.filter((t) => t.toLowerCase() !== slug.toLowerCase()) : [...tags, slug]
            const fd = new FormData()
            if (next.length) next.forEach((t) => fd.append("tags[]", t))
            else fd.append("tags", "")
            const data = await this._patchTask(fd)
            if (data?.ok) this.tagsValue = data.task.tags || []
            return true
        }
        openPickPopover({
            anchor,
            fallback: anchor,
            label: "Tags",
            placeholder: "Search or create a tag",
            search: async (q) => {
                const data = await this._getJson(this._withList(`/tasks/tags?q=${encodeURIComponent(q)}`))
                const mine = this.tagsValue.map((t) => t.toLowerCase())
                return (data?.tags || []).map((tag) => ({ key: tag.slug, label: tag.name || tag.slug, selected: mine.includes(String(tag.slug).toLowerCase()) }))
            },
            onPick: (item) => toggle(item.key),
            create: (name) => toggle(name)
        })
    }

    // One company per pick, as the row's company picker; a new name creates it.
    _pickCompany(anchor) {
        const link = async (body) => {
            const headers = { ...this._headers(), "Content-Type": "application/json" }
            const res = await fetch(this._withList(`/tasks/${this.taskIdValue}/company_link`), { method: "POST", headers, credentials: "same-origin", body: JSON.stringify(body) }).catch(() => null)
            if (res?.ok) this._refreshDetails()
            return false
        }
        openPickPopover({
            anchor,
            fallback: anchor,
            label: "Company",
            placeholder: "Search companies",
            search: async (q) => {
                if (!q) return []
                const data = await this._getJson(`/tasks/mention_candidates?task_id=${this.taskIdValue}&q=${encodeURIComponent(q)}`)
                return (data?.companies || []).map((c) => ({ key: String(c.token).replace("company:", ""), label: c.name, meta: c.meta }))
            },
            onPick: (item) => link({ company_id: item.key }),
            create: (name) => link({ name })
        })
    }

    _chip(candidate) {
        if (candidate.avatar_url) {
            const img = document.createElement("img")
            img.className = "tasks-outline-chip tasks-outline-chip--img"
            img.src = candidate.avatar_url
            img.alt = ""
            return img
        }
        const span = document.createElement("span")
        span.className = "tasks-outline-chip"
        span.style.background = `hsl(${candidate.hue == null ? 210 : candidate.hue}, 60%, 45%)`
        span.textContent = candidate.initials || "?"
        return span
    }

    _getJson(url) {
        return fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" }).then((r) => (r.ok ? r.json() : null)).catch(() => null)
    }

    _withList(url) {
        if (!this.listIdValue) return url
        return `${url}${url.includes("?") ? "&" : "?"}list=${this.listIdValue}`
    }

    // Priority and custom fields are set in the pane itself. On the outline
    // the row writes them (its marker, badges and undo stay right); anywhere
    // else the pane does.
    setPriority(e) {
        e.preventDefault()
        const button = e.currentTarget
        this._pick(button)
        const detail = { taskId: this.taskIdValue, priority: button.dataset.priority, handled: false }
        window.dispatchEvent(new CustomEvent("jane:task-pane-update", { detail }))
        if (detail.handled) return
        const body = new FormData()
        body.set("priority", button.dataset.priority)
        this._patchTask(body)
    }

    setField(e) {
        e.preventDefault()
        const button = e.currentTarget
        this._pick(button)
        const detail = { taskId: this.taskIdValue, fieldId: button.dataset.fieldId, fieldValue: button.dataset.fieldValue, handled: false }
        window.dispatchEvent(new CustomEvent("jane:task-pane-update", { detail }))
        if (detail.handled) return
        const body = new FormData()
        body.append(`field_values[${button.dataset.fieldId}]`, button.dataset.fieldValue || "")
        this._patchTask(body)
    }

    _pick(button) {
        button.closest(".tasks-priority-options")?.querySelectorAll(".tasks-priority-option").forEach((b) => {
            const on = b === button
            b.classList.toggle("is-active", on)
            b.setAttribute("aria-pressed", String(on))
        })
    }

    // The pane's own write; the pane re-reads itself after it.
    _patchTask(body) {
        return fetch(this.notesUrlValue, { method: "PATCH", headers: this._headers(), body, credentials: "same-origin" })
            .then((r) => r.json())
            .catch(() => null)
            .then((data) => {
                if (data?.ok) this._refreshDetails()
                return data
            })
    }

    // The task changed - from the row (complete, priority, a field, a date,
    // the title), a popover, or a teammate's live edit (jane:task-changed):
    // re-read the header (checkbox, title), Details and Subtasks. A beat
    // later so a burst of writes is one fetch; an older reply that arrives
    // after a newer one is dropped. The note and the thread keep their own
    // state - the note may be mid-edit.
    _refreshDetails() {
        clearTimeout(this._refreshTimer)
        this._refreshTimer = setTimeout(() => this._fetchDetails(), 150)
    }

    async _fetchDetails() {
        const seq = (this._refreshSeq = (this._refreshSeq || 0) + 1)
        const url = `/tasks/${this.taskIdValue}/panel?section=details&_=${Date.now()}`
        const html = await fetch(url, { headers: { Accept: "text/html" }, credentials: "same-origin" }).then((r) => (r.ok ? r.text() : null)).catch(() => null)
        if (!html || !this.element.isConnected || seq !== this._refreshSeq) return
        const doc = new DOMParser().parseFromString(html, "text/html")
        const swap = (selector) => {
            const fresh = doc.querySelector(selector)
            const current = this.element.querySelector(selector)
            if (fresh && current) current.innerHTML = fresh.innerHTML
        }
        // The values the pane's own pickers start from.
        const root = doc.querySelector(".task-pane")
        if (root) {
            Array.from(root.attributes).filter((a) => /^data-task-pane-.+-value$/.test(a.name) && a.name !== "data-task-pane-section-value")
                .forEach((a) => this.element.setAttribute(a.name, a.value))
        }
        swap(".task-pane-title-row")
        swap(".task-pane-section[data-section='details']")
        swap(".task-pane-section[data-section='subtasks']")
        const subtasks = this.element.querySelector(".task-pane-section[data-section='subtasks']")
        if (subtasks && doc.querySelector(".task-pane-section[data-section='subtasks']:not(.is-empty)")) {
            subtasks.classList.remove("is-empty")
            if (this._refocusSubtask) subtasks.open = true
        }
        if (this._refocusSubtask && this.hasNewSubtaskTarget) {
            this._refocusSubtask = false
            this.newSubtaskTarget.focus({ preventScroll: true })
        }
        const count = doc.querySelector("[data-task-pane-target='subtaskCount']")
        if (count && this.hasSubtaskCountTarget) this.subtaskCountTarget.textContent = count.textContent
    }

    openTask(e) {
        e.preventDefault()
        const detail = { taskId: Number(e.currentTarget.dataset.taskId), section: "details", handled: false }
        window.dispatchEvent(new CustomEvent("jane:task-pane-open", { detail }))
    }

    // --- Files: the whole pane is the drop target ---

    dragEnter(e) {
        if (!dragHasFiles(e) || !this.hasOverlayTarget) return
        e.preventDefault()
        this.overlayTarget.hidden = false
    }

    dragOver(e) {
        if (!dragHasFiles(e)) return
        e.preventDefault()
        e.dataTransfer.dropEffect = "copy"
        if (this.hasOverlayTarget) this.overlayTarget.hidden = false
    }

    dragLeave(e) {
        if (!this.hasOverlayTarget) return
        if (e.relatedTarget && this.overlayTarget.contains(e.relatedTarget)) return
        this.overlayTarget.hidden = true
    }

    drop(e) {
        if (!dragHasFiles(e)) return
        e.preventDefault()
        if (this.hasOverlayTarget) this.overlayTarget.hidden = true
        this._attach(Array.from(e.dataTransfer.files || []))
    }

    // A screenshot pasted anywhere in the pane - the comment box and the note
    // included - is attached to the task. Text pastes are left alone.
    paste(e) {
        const files = Array.from(e.clipboardData?.files || [])
        if (files.length === 0 || e.clipboardData.getData("text/plain")) return
        e.preventDefault()
        this._attach(files)
    }

    _attach(files) {
        if (!files.length) return
        const progress = this.element.querySelector("[data-task-attachments-target='progress']")
        const error = this.element.querySelector("[data-task-attachments-target='error']")
        if (progress) progress.hidden = false
        this._scrollTo("files")
        uploadTaskAttachments(this.taskIdValue, files).then((data) => {
            if (progress) progress.hidden = true
            if (data?.error && error) {
                error.textContent = data.error
                error.hidden = false
                setTimeout(() => { error.hidden = true }, 6000)
            }
        })
    }

    _headers() {
        const headers = { Accept: "application/json", "X-CSRF-Token": csrfToken() }
        const clientId = document.querySelector("[data-task-list-client-id]")?.dataset.taskListClientId
        if (clientId) headers["X-Task-List-Client-Id"] = clientId
        return headers
    }
}
